export type CardSuit = 'cups' | 'swords' | 'coins' | 'wands';
export type Arcana = 'major' | `minor-${CardSuit}`;

interface BaseCardDescription {
  /** Stable identifier, e.g. `the-fool` or `ace-of-cups`. */
  id: string;
  /** 0–21 for the Major Arcana; 1 (ace) – 14 (king) for the Minor Arcana. */
  number: number;
  name: string;
  keywords: readonly string[];
  upright: string;
  reversed: string;
}

export interface MajorArcanaCard extends BaseCardDescription {
  arcana: 'major';
  roman: string;
  suit?: undefined;
}

export interface MinorArcanaCard extends BaseCardDescription {
  arcana: `minor-${CardSuit}`;
  suit: CardSuit;
  roman?: undefined;
}

export type CardDescription = MajorArcanaCard | MinorArcanaCard;

export const MAJOR_ARCANA: readonly MajorArcanaCard[] = [
  {
    id: 'the-fool',
    number: 0,
    roman: '0',
    name: 'The Fool',
    arcana: 'major',
    keywords: ['beginnings', 'innocence', 'spontaneity', 'free spirit'],
    upright: 'New beginnings, optimism, and a leap of faith into the unknown.',
    reversed: 'Recklessness, naivety, and ignoring warning signs.',
  },
  {
    id: 'the-magician',
    number: 1,
    roman: 'I',
    name: 'The Magician',
    arcana: 'major',
    keywords: ['manifestation', 'willpower', 'resourcefulness', 'focus'],
    upright: 'You have the tools to make your vision real — act with intent.',
    reversed: 'Manipulation, scattered focus, or untapped potential.',
  },
  {
    id: 'the-high-priestess',
    number: 2,
    roman: 'II',
    name: 'The High Priestess',
    arcana: 'major',
    keywords: ['intuition', 'mystery', 'subconscious', 'inner voice'],
    upright: 'Trust your intuition; the answers lie beneath the surface.',
    reversed: 'Disconnection from instinct, secrets withheld, repressed feelings.',
  },
  {
    id: 'the-empress',
    number: 3,
    roman: 'III',
    name: 'The Empress',
    arcana: 'major',
    keywords: ['abundance', 'nurturing', 'creativity', 'fertility'],
    upright: 'Creativity blooms; nurture what you have been building.',
    reversed: 'Creative block, dependence, or neglect of self-care.',
  },
  {
    id: 'the-emperor',
    number: 4,
    roman: 'IV',
    name: 'The Emperor',
    arcana: 'major',
    keywords: ['authority', 'structure', 'leadership', 'stability'],
    upright: 'Stability through structure and disciplined leadership.',
    reversed: 'Rigidity, domination, or a loss of control.',
  },
  {
    id: 'the-hierophant',
    number: 5,
    roman: 'V',
    name: 'The Hierophant',
    arcana: 'major',
    keywords: ['tradition', 'wisdom', 'institutions', 'belief'],
    upright: 'Seek guidance from tradition, mentors, or established wisdom.',
    reversed: 'Question dogma; the old ways may no longer serve you.',
  },
  {
    id: 'the-lovers',
    number: 6,
    roman: 'VI',
    name: 'The Lovers',
    arcana: 'major',
    keywords: ['union', 'choice', 'values', 'harmony'],
    upright: 'A meaningful choice or union rooted in shared values.',
    reversed: 'Misalignment, disharmony, or avoiding a real decision.',
  },
  {
    id: 'the-chariot',
    number: 7,
    roman: 'VII',
    name: 'The Chariot',
    arcana: 'major',
    keywords: ['willpower', 'victory', 'direction', 'determination'],
    upright: 'Move forward with focus and discipline — victory is earned.',
    reversed: 'Scattered effort, opposition, or loss of direction.',
  },
  {
    id: 'strength',
    number: 8,
    roman: 'VIII',
    name: 'Strength',
    arcana: 'major',
    keywords: ['courage', 'compassion', 'patience', 'inner power'],
    upright: 'Quiet courage and compassion tame what force cannot.',
    reversed: 'Self-doubt, raw emotion, or insecurity overrunning reason.',
  },
  {
    id: 'the-hermit',
    number: 9,
    roman: 'IX',
    name: 'The Hermit',
    arcana: 'major',
    keywords: ['introspection', 'solitude', 'guidance', 'inner light'],
    upright: 'Withdraw and reflect; the lantern within reveals the path.',
    reversed: 'Isolation, withdrawal that becomes loneliness, lost guidance.',
  },
  {
    id: 'wheel-of-fortune',
    number: 10,
    roman: 'X',
    name: 'Wheel of Fortune',
    arcana: 'major',
    keywords: ['cycles', 'turning point', 'fate', 'change'],
    upright: 'A turning point — fortune favors those ready for change.',
    reversed: 'Resistance to the cycle, bad timing, or feeling stuck.',
  },
  {
    id: 'justice',
    number: 11,
    roman: 'XI',
    name: 'Justice',
    arcana: 'major',
    keywords: ['fairness', 'truth', 'accountability', 'cause and effect'],
    upright: 'Truth and fairness prevail; choices have consequences.',
    reversed: 'Dishonesty, unaccountability, or avoiding the truth.',
  },
  {
    id: 'the-hanged-man',
    number: 12,
    roman: 'XII',
    name: 'The Hanged Man',
    arcana: 'major',
    keywords: ['surrender', 'pause', 'new perspective', 'sacrifice'],
    upright: 'Pause and shift your perspective — surrender reveals insight.',
    reversed: 'Stalling, indecision, or pointless martyrdom.',
  },
  {
    id: 'death',
    number: 13,
    roman: 'XIII',
    name: 'Death',
    arcana: 'major',
    keywords: ['ending', 'transformation', 'release', 'transition'],
    upright: 'An ending makes way for transformation — let go to grow.',
    reversed: 'Clinging to what is over; resistance to needed change.',
  },
  {
    id: 'temperance',
    number: 14,
    roman: 'XIV',
    name: 'Temperance',
    arcana: 'major',
    keywords: ['balance', 'moderation', 'patience', 'purpose'],
    upright: 'Patience and balance blend opposites into purpose.',
    reversed: 'Excess, imbalance, or impatience disrupting harmony.',
  },
  {
    id: 'the-devil',
    number: 15,
    roman: 'XV',
    name: 'The Devil',
    arcana: 'major',
    keywords: ['shadow', 'attachment', 'restriction', 'temptation'],
    upright: 'Confront what binds you — desire, habit, or fear.',
    reversed: 'Releasing chains, reclaiming power, breaking patterns.',
  },
  {
    id: 'the-tower',
    number: 16,
    roman: 'XVI',
    name: 'The Tower',
    arcana: 'major',
    keywords: ['upheaval', 'revelation', 'awakening', 'sudden change'],
    upright: 'Sudden upheaval shatters what was built on false ground.',
    reversed: 'Disaster averted, or fear of inevitable change.',
  },
  {
    id: 'the-star',
    number: 17,
    roman: 'XVII',
    name: 'The Star',
    arcana: 'major',
    keywords: ['hope', 'renewal', 'inspiration', 'serenity'],
    upright: 'After the storm: hope, renewal, and quiet inspiration.',
    reversed: 'Despondency, lost faith, or feeling adrift.',
  },
  {
    id: 'the-moon',
    number: 18,
    roman: 'XVIII',
    name: 'The Moon',
    arcana: 'major',
    keywords: ['illusion', 'intuition', 'fear', 'subconscious'],
    upright: 'Things are not as they seem; trust intuition over appearance.',
    reversed: 'Confusion lifting, fears releasing, clarity returning.',
  },
  {
    id: 'the-sun',
    number: 19,
    roman: 'XIX',
    name: 'The Sun',
    arcana: 'major',
    keywords: ['joy', 'vitality', 'success', 'clarity'],
    upright: 'Joy, vitality, and well-deserved success light your path.',
    reversed: 'A temporary cloud; check in with your inner child.',
  },
  {
    id: 'judgement',
    number: 20,
    roman: 'XX',
    name: 'Judgement',
    arcana: 'major',
    keywords: ['awakening', 'reckoning', 'rebirth', 'calling'],
    upright: 'A calling stirs — review the past and rise renewed.',
    reversed: 'Self-doubt, harsh self-judgement, ignoring the call.',
  },
  {
    id: 'the-world',
    number: 21,
    roman: 'XXI',
    name: 'The World',
    arcana: 'major',
    keywords: ['completion', 'fulfillment', 'integration', 'wholeness'],
    upright: 'A cycle completes; integration and fulfillment arrive.',
    reversed: 'Loose ends, almost-there, or skipping the final step.',
  },
];

// CUPS - Emotions, Relationships, Creativity
export const CUPS: readonly MinorArcanaCard[] = [
  {
    id: 'ace-of-cups',
    number: 1,
    name: 'Ace of Cups',
    arcana: 'minor-cups',
    suit: 'cups',
    keywords: ['new emotion', 'love', 'intuition', 'gift'],
    upright: 'A new emotional beginning — an offer of love or creative inspiration.',
    reversed: 'Blocked emotions, rejected love, or creative drought.',
  },
  {
    id: 'two-of-cups',
    number: 2,
    name: 'Two of Cups',
    arcana: 'minor-cups',
    suit: 'cups',
    keywords: ['union', 'intimacy', 'partnership', 'mutual respect'],
    upright: 'Emotional balance and mutual respect in partnership.',
    reversed: 'Imbalance, broken trust, or relationship difficulty.',
  },
  {
    id: 'three-of-cups',
    number: 3,
    name: 'Three of Cups',
    arcana: 'minor-cups',
    suit: 'cups',
    keywords: ['celebration', 'community', 'friendship', 'social joy'],
    upright: 'Celebration and true friendship gather together.',
    reversed: 'Isolation, excess, or superficial connection.',
  },
  {
    id: 'four-of-cups',
    number: 4,
    name: 'Four of Cups',
    arcana: 'minor-cups',
    suit: 'cups',
    keywords: ['apathy', 'contemplation', 'indifference', 'opportunity'],
    upright: 'An offer appears, but contemplation delays acceptance.',
    reversed: 'Accepting the offer; moving past apathy.',
  },
  {
    id: 'five-of-cups',
    number: 5,
    name: 'Five of Cups',
    arcana: 'minor-cups',
    suit: 'cups',
    keywords: ['loss', 'grief', 'sorrow', 'regret'],
    upright: 'Sorrow and loss — yet some light remains.',
    reversed: 'Healing begins; moving past grief.',
  },
  {
    id: 'six-of-cups',
    number: 6,
    name: 'Six of Cups',
    arcana: 'minor-cups',
    suit: 'cups',
    keywords: ['generosity', 'innocence', 'nostalgia', 'kindness'],
    upright: 'Innocent joy and generous connection return.',
    reversed: 'Lingering in the past; blocked generosity.',
  },
  {
    id: 'seven-of-cups',
    number: 7,
    name: 'Seven of Cups',
    arcana: 'minor-cups',
    suit: 'cups',
    keywords: ['illusion', 'choice', 'fantasy', 'temptation'],
    upright: 'Many paths tempt; illusion and wish-thinking cloud choice.',
    reversed: 'Clear sight; true priorities emerge.',
  },
  {
    id: 'eight-of-cups',
    number: 8,
    name: 'Eight of Cups',
    arcana: 'minor-cups',
    suit: 'cups',
    keywords: ['abandonment', 'leave-taking', 'search', 'spiritual quest'],
    upright: 'Walking away toward something deeper and more true.',
    reversed: 'Fear of abandonment; refusing to move on.',
  },
  {
    id: 'nine-of-cups',
    number: 9,
    name: 'Nine of Cups',
    arcana: 'minor-cups',
    suit: 'cups',
    keywords: ['satisfaction', 'abundance', 'gratitude', 'contentment'],
    upright: 'Wishes fulfilled and dreams realized in contentment.',
    reversed: 'Overindulgence or shallow satisfaction.',
  },
  {
    id: 'ten-of-cups',
    number: 10,
    name: 'Ten of Cups',
    arcana: 'minor-cups',
    suit: 'cups',
    keywords: ['harmony', 'happiness', 'family', 'celebration'],
    upright: 'Family harmony and lasting happiness gather under one roof.',
    reversed: 'Broken family bonds; seeking belonging.',
  },
  {
    id: 'page-of-cups',
    number: 11,
    name: 'Page of Cups',
    arcana: 'minor-cups',
    suit: 'cups',
    keywords: ['creativity', 'sensitivity', 'idealism', 'curiosity'],
    upright: 'A sensitive dreamer bringing artistic inspiration.',
    reversed: 'Scattered emotions or creative self-doubt.',
  },
  {
    id: 'knight-of-cups',
    number: 12,
    name: 'Knight of Cups',
    arcana: 'minor-cups',
    suit: 'cups',
    keywords: ['romance', 'charm', 'adventure', 'idealism'],
    upright: 'A charming romantic, idealistic and emotionally expressive.',
    reversed: 'Emotional manipulation or unrealistic promises.',
  },
  {
    id: 'queen-of-cups',
    number: 13,
    name: 'Queen of Cups',
    arcana: 'minor-cups',
    suit: 'cups',
    keywords: ['compassion', 'intuition', 'nurturing', 'emotional depth'],
    upright: 'Emotional wisdom and intuitive compassion guide.',
    reversed: 'Emotional overwhelm or self-care neglect.',
  },
  {
    id: 'king-of-cups',
    number: 14,
    name: 'King of Cups',
    arcana: 'minor-cups',
    suit: 'cups',
    keywords: ['emotional control', 'compassion', 'balance', 'diplomacy'],
    upright: 'Emotional balance and compassionate authority.',
    reversed: 'Emotional instability or cold distance.',
  },
];

// SWORDS - Conflict, Intellect, Communication
export const SWORDS: readonly MinorArcanaCard[] = [
  {
    id: 'ace-of-swords',
    number: 1,
    name: 'Ace of Swords',
    arcana: 'minor-swords',
    suit: 'swords',
    keywords: ['clarity', 'truth', 'clarity', 'breakthrough'],
    upright: 'Truth cuts through confusion; clarity and breakthrough arrive.',
    reversed: 'Confusion lingers; difficulty seeing clearly.',
  },
  {
    id: 'two-of-swords',
    number: 2,
    name: 'Two of Swords',
    arcana: 'minor-swords',
    suit: 'swords',
    keywords: ['stalemate', 'indecision', 'deadlock', 'standoff'],
    upright: 'Deadlock and difficult choice between equal options.',
    reversed: 'Resolution arrives; clarity breaks the stalemate.',
  },
  {
    id: 'three-of-swords',
    number: 3,
    name: 'Three of Swords',
    arcana: 'minor-swords',
    suit: 'swords',
    keywords: ['heartbreak', 'sorrow', 'grief', 'separation'],
    upright: 'Painful truth and heartbreak must be faced.',
    reversed: 'Healing; moving past pain and separation.',
  },
  {
    id: 'four-of-swords',
    number: 4,
    name: 'Four of Swords',
    arcana: 'minor-swords',
    suit: 'swords',
    keywords: ['rest', 'recovery', 'truce', 'pause'],
    upright: 'Rest and recovery after conflict; a necessary pause.',
    reversed: 'Restlessness; struggle returning too soon.',
  },
  {
    id: 'five-of-swords',
    number: 5,
    name: 'Five of Swords',
    arcana: 'minor-swords',
    suit: 'swords',
    keywords: ['defeat', 'loss', 'tension', 'conflict'],
    upright: 'Conflict leaves scars; a hollow victory.',
    reversed: 'Making peace; releasing hostility.',
  },
  {
    id: 'six-of-swords',
    number: 6,
    name: 'Six of Swords',
    arcana: 'minor-swords',
    suit: 'swords',
    keywords: ['departure', 'travel', 'escape', 'transition'],
    upright: 'Moving toward calmer waters; journey and transition.',
    reversed: 'Stuck in the past; difficulty moving forward.',
  },
  {
    id: 'seven-of-swords',
    number: 7,
    name: 'Seven of Swords',
    arcana: 'minor-swords',
    suit: 'swords',
    keywords: ['deception', 'theft', 'betrayal', 'cunning'],
    upright: 'Betrayal and deception lurk; question intentions.',
    reversed: 'Truth emerges; dishonesty exposed.',
  },
  {
    id: 'eight-of-swords',
    number: 8,
    name: 'Eight of Swords',
    arcana: 'minor-swords',
    suit: 'swords',
    keywords: ['restriction', 'powerlessness', 'self-limitation', 'trapped'],
    upright: 'Feeling trapped, but the bonds are illusory.',
    reversed: 'Breaking free; reclaiming agency.',
  },
  {
    id: 'nine-of-swords',
    number: 9,
    name: 'Nine of Swords',
    arcana: 'minor-swords',
    suit: 'swords',
    keywords: ['anxiety', 'nightmares', 'worry', 'despair'],
    upright: 'Worry and dark thoughts torment; seek perspective.',
    reversed: 'Anxiety eases; peace of mind returns.',
  },
  {
    id: 'ten-of-swords',
    number: 10,
    name: 'Ten of Swords',
    arcana: 'minor-swords',
    suit: 'swords',
    keywords: ['ending', 'betrayal', 'ruin', 'final crisis'],
    upright: 'An ending; the worst has passed.',
    reversed: 'Beginning to heal; crisis subsiding.',
  },
  {
    id: 'page-of-swords',
    number: 11,
    name: 'Page of Swords',
    arcana: 'minor-swords',
    suit: 'swords',
    keywords: ['curiosity', 'inquiry', 'communication', 'youthful spirit'],
    upright: 'Curious questioning; a seeker of truth and communication.',
    reversed: 'Careless words; unfounded accusations.',
  },
  {
    id: 'knight-of-swords',
    number: 12,
    name: 'Knight of Swords',
    arcana: 'minor-swords',
    suit: 'swords',
    keywords: ['intellect', 'logic', 'clarity', 'conflict'],
    upright: 'Sharp intellect and direct communication.',
    reversed: 'Argumentative nature; verbal conflict.',
  },
  {
    id: 'queen-of-swords',
    number: 13,
    name: 'Queen of Swords',
    arcana: 'minor-swords',
    suit: 'swords',
    keywords: ['clarity', 'independence', 'intellect', 'authority'],
    upright: 'Clear thinking and independent judgment lead.',
    reversed: 'Harsh criticism or emotional detachment.',
  },
  {
    id: 'king-of-swords',
    number: 14,
    name: 'King of Swords',
    arcana: 'minor-swords',
    suit: 'swords',
    keywords: ['authority', 'intellect', 'logic', 'truth'],
    upright: 'Intellectual authority and truthful judgment prevail.',
    reversed: 'Abuse of power or harsh discipline.',
  },
];

// COINS (PENTACLES) - Wealth, Work, Material Realm
export const COINS: readonly MinorArcanaCard[] = [
  {
    id: 'ace-of-coins',
    number: 1,
    name: 'Ace of Coins',
    arcana: 'minor-coins',
    suit: 'coins',
    keywords: ['opportunity', 'prosperity', 'new start', 'gift'],
    upright: 'A new opportunity for abundance and prosperity.',
    reversed: 'Missed opportunity or blocked prosperity.',
  },
  {
    id: 'two-of-coins',
    number: 2,
    name: 'Two of Coins',
    arcana: 'minor-coins',
    suit: 'coins',
    keywords: ['balance', 'juggling', 'adaptability', 'flexibility'],
    upright: 'Balance multiple demands with grace and flexibility.',
    reversed: 'Juggling too much; imbalance and overwhelm.',
  },
  {
    id: 'three-of-coins',
    number: 3,
    name: 'Three of Coins',
    arcana: 'minor-coins',
    suit: 'coins',
    keywords: ['collaboration', 'skill', 'teamwork', 'learning'],
    upright: 'Skilled collaboration and mastery through teamwork.',
    reversed: 'Lack of teamwork; poor communication at work.',
  },
  {
    id: 'four-of-coins',
    number: 4,
    name: 'Four of Coins',
    arcana: 'minor-coins',
    suit: 'coins',
    keywords: ['control', 'possessiveness', 'security', 'stinginess'],
    upright: 'Holding tight to what you have; security-seeking.',
    reversed: 'Releasing control; learning to share and receive.',
  },
  {
    id: 'five-of-coins',
    number: 5,
    name: 'Five of Coins',
    arcana: 'minor-coins',
    suit: 'coins',
    keywords: ['hardship', 'poverty', 'struggle', 'isolation'],
    upright: 'Financial hardship and isolation; yet help is near.',
    reversed: 'Hardship easing; recovery underway.',
  },
  {
    id: 'six-of-coins',
    number: 6,
    name: 'Six of Coins',
    arcana: 'minor-coins',
    suit: 'coins',
    keywords: ['generosity', 'charity', 'sharing', 'fairness'],
    upright: 'Generosity and fair exchange; helping others.',
    reversed: 'Selfishness or imbalance in giving and receiving.',
  },
  {
    id: 'seven-of-coins',
    number: 7,
    name: 'Seven of Coins',
    arcana: 'minor-coins',
    suit: 'coins',
    keywords: ['investment', 'effort', 'assessment', 're-evaluation'],
    upright: 'Assessing effort invested; patience in growth.',
    reversed: 'Wasted effort or lack of tangible progress.',
  },
  {
    id: 'eight-of-coins',
    number: 8,
    name: 'Eight of Coins',
    arcana: 'minor-coins',
    suit: 'coins',
    keywords: ['mastery', 'apprenticeship', 'skill', 'practice'],
    upright: 'Dedication to craft; mastery through disciplined practice.',
    reversed: 'Lack of discipline or superficial learning.',
  },
  {
    id: 'nine-of-coins',
    number: 9,
    name: 'Nine of Coins',
    arcana: 'minor-coins',
    suit: 'coins',
    keywords: ['independence', 'prosperity', 'luxury', 'reward'],
    upright: 'Prosperity earned through effort; independence and reward.',
    reversed: 'Financial loss or dependence on others.',
  },
  {
    id: 'ten-of-coins',
    number: 10,
    name: 'Ten of Coins',
    arcana: 'minor-coins',
    suit: 'coins',
    keywords: ['wealth', 'family', 'inheritance', 'security'],
    upright: 'Generational wealth and family security established.',
    reversed: 'Family conflict over finances or inheritance blocked.',
  },
  {
    id: 'page-of-coins',
    number: 11,
    name: 'Page of Coins',
    arcana: 'minor-coins',
    suit: 'coins',
    keywords: ['student', 'enthusiasm', 'diligence', 'practical'],
    upright: 'An earnest student of practical skills.',
    reversed: 'Lack of direction or unfocused energy.',
  },
  {
    id: 'knight-of-coins',
    number: 12,
    name: 'Knight of Coins',
    arcana: 'minor-coins',
    suit: 'coins',
    keywords: ['reliability', 'dedication', 'hard work', 'loyalty'],
    upright: 'Reliable and steady dedication to duty and work.',
    reversed: 'Laziness or lack of follow-through.',
  },
  {
    id: 'queen-of-coins',
    number: 13,
    name: 'Queen of Coins',
    arcana: 'minor-coins',
    suit: 'coins',
    keywords: ['nurturing', 'abundance', 'practicality', 'comfort'],
    upright: 'Nurturing abundance and creating comfort and security.',
    reversed: 'Neglect or materialism overriding heart.',
  },
  {
    id: 'king-of-coins',
    number: 14,
    name: 'King of Coins',
    arcana: 'minor-coins',
    suit: 'coins',
    keywords: ['wealth', 'authority', 'abundance', 'leadership'],
    upright: 'Leadership through material mastery and wealth.',
    reversed: 'Greed or abuse of financial power.',
  },
];

// WANDS - Energy, Passion, Action, Creativity
export const WANDS: readonly MinorArcanaCard[] = [
  {
    id: 'ace-of-wands',
    number: 1,
    name: 'Ace of Wands',
    arcana: 'minor-wands',
    suit: 'wands',
    keywords: ['inspiration', 'passion', 'new beginnings', 'potential'],
    upright: 'A spark of passion and creative potential ignites.',
    reversed: 'Blocked inspiration or lack of direction.',
  },
  {
    id: 'two-of-wands',
    number: 2,
    name: 'Two of Wands',
    arcana: 'minor-wands',
    suit: 'wands',
    keywords: ['planning', 'decision', 'possible paths', 'vision'],
    upright: 'Vision and planning; choosing the path forward.',
    reversed: 'Indecision or blocked potential.',
  },
  {
    id: 'three-of-wands',
    number: 3,
    name: 'Three of Wands',
    arcana: 'minor-wands',
    suit: 'wands',
    keywords: ['foresight', 'expansion', 'collaboration', 'vision'],
    upright: 'Progress and expansion through foresight and planning.',
    reversed: 'Delays in plans; lack of momentum.',
  },
  {
    id: 'four-of-wands',
    number: 4,
    name: 'Four of Wands',
    arcana: 'minor-wands',
    suit: 'wands',
    keywords: ['celebration', 'stability', 'joy', 'foundation'],
    upright: 'Celebration and stability; a milestone reached.',
    reversed: 'Instability or incomplete celebration.',
  },
  {
    id: 'five-of-wands',
    number: 5,
    name: 'Five of Wands',
    arcana: 'minor-wands',
    suit: 'wands',
    keywords: ['conflict', 'challenge', 'competition', 'struggle'],
    upright: 'Conflict and challenge drive growth.',
    reversed: 'Conflict easing; harmony returning.',
  },
  {
    id: 'six-of-wands',
    number: 6,
    name: 'Six of Wands',
    arcana: 'minor-wands',
    suit: 'wands',
    keywords: ['victory', 'success', 'recognition', 'approval'],
    upright: 'Recognition and success; hard work pays off.',
    reversed: 'Lack of recognition or false victory.',
  },
  {
    id: 'seven-of-wands',
    number: 7,
    name: 'Seven of Wands',
    arcana: 'minor-wands',
    suit: 'wands',
    keywords: ['competition', 'perseverance', 'defend', 'challenge'],
    upright: 'Stand your ground; challenge strengthens resolve.',
    reversed: 'Surrender or lack of conviction.',
  },
  {
    id: 'eight-of-wands',
    number: 8,
    name: 'Eight of Wands',
    arcana: 'minor-wands',
    suit: 'wands',
    keywords: ['movement', 'speed', 'momentum', 'progress'],
    upright: 'Rapid progress and swift movement forward.',
    reversed: 'Delays or blockages slowing momentum.',
  },
  {
    id: 'nine-of-wands',
    number: 9,
    name: 'Nine of Wands',
    arcana: 'minor-wands',
    suit: 'wands',
    keywords: ['resilience', 'persistence', 'strength', 'resolve'],
    upright: 'Last stand and resilience; nearly there.',
    reversed: 'Exhaustion; giving up near the finish line.',
  },
  {
    id: 'ten-of-wands',
    number: 10,
    name: 'Ten of Wands',
    arcana: 'minor-wands',
    suit: 'wands',
    keywords: ['burden', 'responsibility', 'completion', 'weight'],
    upright: 'Heavy responsibility; the final burden before completion.',
    reversed: 'Letting go of burden; relief coming.',
  },
  {
    id: 'page-of-wands',
    number: 11,
    name: 'Page of Wands',
    arcana: 'minor-wands',
    suit: 'wands',
    keywords: ['enthusiasm', 'youth', 'adventure', 'potential'],
    upright: 'Youthful enthusiasm and adventurous spirit.',
    reversed: 'Impatience or scattered energy.',
  },
  {
    id: 'knight-of-wands',
    number: 12,
    name: 'Knight of Wands',
    arcana: 'minor-wands',
    suit: 'wands',
    keywords: ['passion', 'energy', 'adventure', 'zeal'],
    upright: 'Passionate adventurer and bold risk-taker.',
    reversed: 'Reckless or unfocused energy.',
  },
  {
    id: 'queen-of-wands',
    number: 13,
    name: 'Queen of Wands',
    arcana: 'minor-wands',
    suit: 'wands',
    keywords: ['confidence', 'independence', 'magnetism', 'vibrancy'],
    upright: 'Confident, magnetic charisma and creative independence.',
    reversed: 'Self-doubt or attention-seeking behavior.',
  },
  {
    id: 'king-of-wands',
    number: 14,
    name: 'King of Wands',
    arcana: 'minor-wands',
    suit: 'wands',
    keywords: ['leadership', 'vision', 'passion', 'boldness'],
    upright: 'Visionary leadership and bold passion.',
    reversed: 'Arrogance or destructive impulse.',
  },
];
